import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'

export interface TooltipProps {
  /** Texto em pt-BR. Aparece no mesmo frame do hover ou do foco, sem atraso. */
  label: string
  children: ReactNode
}

const MARGIN = 8
const TOOLTIP_WIDTH = 220
/** Toque recente o bastante para o foco ter vindo dele. */
const TOUCH_FOCUS_WINDOW_MS = 1000
/** Só entra na pilha se continuar aberta depois do toque (não a cada toque que dispara uma ação). */
const TOUCH_PIN_DELAY_MS = 400

/**
 * Tooltip do design system. Não usa o atributo `title` (o nativo espera ~2s).
 * Vale para todo controle que passe `title` em `Button` ou `GuardedNavLink`.
 * No celular o balão fica inteiro dentro da viewport.
 */
export const Tooltip = ({ label, children }: TooltipProps) => {
  const [open, setOpen] = useState(false)
  const [box, setBox] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null)
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const tooltipRef = useRef<HTMLSpanElement | null>(null)
  const id = useId()
  const valid = isValidElement(children)
  const portalRoot = typeof document === 'undefined' ? null : document.fullscreenElement ?? document.body
  /** `timeStamp` do último pointerenter por toque (0 = não foi toque). */
  const [lastTouchAt, setLastTouchAt] = useState(0)
  /** Aberta por toque (foco logo após um toque), não por hover/teclado. */
  const [touchOpen, setTouchOpen] = useState(false)
  const [touchPinned, setTouchPinned] = useState(false)

  useEffect(() => {
    if (!open || !touchOpen) return
    const timer = window.setTimeout(() => setTouchPinned(true), TOUCH_PIN_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
      setTouchPinned(false)
    }
  }, [open, touchOpen])

  // Tooltip aberta por toque (Q1): voltar (web e nativo) e Esc fecham — pilha única de overlays.
  useOverlay(open && touchPinned, {
    kind: 'popover',
    label,
    restoreFocus: false,
    onRequestClose: () => {
      setOpen(false)
      setTouchOpen(false)
    },
  })

  useLayoutEffect(() => {
    if (!open || !valid) return
    const anchor = anchorRef.current
    if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return
    const viewport = window.visualViewport
    const reposition = () => {
      const tooltip = tooltipRef.current
      if (!tooltip) return
      const rect = anchor.getBoundingClientRect()
      const viewportLeft = viewport?.offsetLeft ?? 0
      const viewportTop = viewport?.offsetTop ?? 0
      const viewportWidth = viewport?.width ?? window.innerWidth
      const viewportHeight = viewport?.height ?? window.innerHeight
      const width = Math.min(TOOLTIP_WIDTH, Math.max(0, viewportWidth - MARGIN * 2))
      const maxHeight = Math.max(0, viewportHeight - MARGIN * 2)
      // Measure after wrapping to the available width, including pinch zoom.
      tooltip.style.width = `${width}px`
      tooltip.style.maxHeight = `${maxHeight}px`
      const height = Math.min(tooltip.getBoundingClientRect().height, maxHeight)
      const left = Math.max(viewportLeft + MARGIN, Math.min(
        rect.left + rect.width / 2 - width / 2,
        viewportLeft + viewportWidth - width - MARGIN,
      ))
      const preferredTop = rect.top - MARGIN - height >= viewportTop + MARGIN
        ? rect.top - MARGIN - height
        : rect.bottom + MARGIN
      const top = Math.max(viewportTop + MARGIN, Math.min(
        preferredTop, viewportTop + viewportHeight - height - MARGIN,
      ))
      setBox({ top, left, width, maxHeight })
    }
    reposition()
    document.addEventListener('fullscreenchange', reposition)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    viewport?.addEventListener('resize', reposition)
    viewport?.addEventListener('scroll', reposition)
    return () => {
      document.removeEventListener('fullscreenchange', reposition)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
      viewport?.removeEventListener('resize', reposition)
      viewport?.removeEventListener('scroll', reposition)
    }
  }, [open, label, valid, portalRoot])

  if (!valid) return children

  const child = children as ReactElement<Record<string, unknown>>
  const describedBy = [child.props['aria-describedby'], open ? id : null].filter(Boolean).join(' ') || undefined

  const tooltip =
    open && portalRoot
      ? createPortal(
          <span
            ref={tooltipRef}
            id={id}
            role="tooltip"
            className="pointer-events-none fixed z-[200] max-w-[min(13.75rem,calc(100vw-1rem))] whitespace-normal break-words overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-center text-xs font-medium text-[var(--color-text-primary)] shadow-sm"
            style={{
              top: box?.top ?? 0,
              left: box?.left ?? 0,
              width: box?.width ?? TOOLTIP_WIDTH,
              maxHeight: box?.maxHeight,
              visibility: box ? 'visible' : 'hidden',
            }}
          >
            {label}
          </span>,
          portalRoot,
        )
      : null

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex w-full max-w-full"
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(event) => {
        const next = event.relatedTarget
        if (next instanceof Node && event.currentTarget.contains(next)) return
        setOpen(false)
      }}
    >
      {cloneElement(child, {
        'aria-describedby': describedBy,
        onPointerEnter: (event: PointerEvent) => {
          setLastTouchAt(event.pointerType === 'touch' ? event.timeStamp : 0)
          setOpen(true)
          const previous = child.props.onPointerEnter as ((event: PointerEvent) => void) | undefined
          previous?.(event)
        },
        onPointerLeave: (event: PointerEvent) => {
          setOpen(false)
          const previous = child.props.onPointerLeave as ((event: PointerEvent) => void) | undefined
          previous?.(event)
        },
        onFocus: (event: FocusEvent) => {
          setOpen(true)
          setTouchOpen(lastTouchAt > 0 && event.timeStamp - lastTouchAt < TOUCH_FOCUS_WINDOW_MS)
          const previous = child.props.onFocus as ((event: FocusEvent) => void) | undefined
          previous?.(event)
        },
        onBlur: (event: FocusEvent) => {
          setOpen(false)
          setTouchOpen(false)
          const previous = child.props.onBlur as ((event: FocusEvent) => void) | undefined
          previous?.(event)
        },
        onKeyDown: (event: KeyboardEvent) => {
          // Fixada por toque, o Esc vem da pilha única (`overlayKeyboard`).
          if (event.key === 'Escape' && !touchPinned) setOpen(false)
          const previous = child.props.onKeyDown as ((event: KeyboardEvent) => void) | undefined
          previous?.(event)
        },
      })}
      {tooltip}
    </span>
  )
}
